import React, { useState, useRef, useEffect, ReactNode, ReactElement } from "react";

interface DropdownProps {
  button: React.ReactNode; // The button that triggers the dropdown
  children: ReactNode; // Dropdown content
  dropdownClass?: string;
}

const Dropdown: React.FC<DropdownProps> = ({ button, children, dropdownClass }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Refs for dropdown and button
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  // Function to toggle dropdown visibility
  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  // Close dropdown if click is outside the dropdown or button
  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking on any child
  const handleChildClick = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block">
      {/* Trigger Button */}
      <div ref={buttonRef} onClick={toggleDropdown} className="cursor-pointer">
        {button}
      </div>

      {/* Dropdown Content */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute right-0 mt-2 z-10 bg-white rounded-lg box-shadow w-60 ${dropdownClass}`}
        >
          {/* Wrap children to add click handling */}
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child as ReactElement, {
                onClick: (e: React.MouseEvent) => {
                  if (child.props.onClick) {
                    child.props.onClick(e); // Call child's original onClick
                  }
                  handleChildClick(); // Close dropdown
                },
              });
            }
            return child;
          })}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
